'use client';

import { CSSProperties, ReactNode, useEffect, useRef } from 'react';

interface RevealOnScrollProps {
  children: ReactNode;
  /** Delay before the animation starts, in ms. Useful for staggering siblings. */
  delay?: number;
  /** Animation duration, in ms. */
  duration?: number;
  /** Vertical offset of the initial state, in px. */
  distance?: number;
  /** Extra classes applied to the wrapper. */
  className?: string;
  /** Extra inline styles applied to the wrapper (merged before animation styles). */
  style?: CSSProperties;
}

/**
 * Wraps its children in a div that fades in + slides up the first time it
 * enters the viewport. Honors `prefers-reduced-motion: reduce`.
 *
 * Pattern mirrors `ProcessOverviewThreeStep`'s IntersectionObserver setup so
 * motion stays consistent across the site.
 */
export const RevealOnScroll = ({
  children,
  delay = 0,
  duration = 700,
  distance = 32,
  className,
  style,
}: RevealOnScrollProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: 0,
        transform: `translateY(${distance}px)`,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
};

export default RevealOnScroll;
